import { useCallback, useState } from 'react';
import { createSelectionEntry } from './neoFeedContextUtils';

/**
 * Estado e handlers de seleção de fontes NeoFeed (notícia + chunks + modo de
 * uso), usados pelos painéis "Sugestões NeoFeed" nas telas de escrita de
 * matéria nova, matéria existente e inteligência editorial.
 *
 * @typedef {import('../../../types/articles').NeoFeedContextSelectionEntry} NeoFeedContextSelectionEntry
 * @param {{ forceUsageMode?: import('../../../types/articles').NeoFeedUsageMode }} [options]
 *   `forceUsageMode` fixa o modo de uso ao selecionar uma sugestão (usado
 *   pelos painéis simplificados, que não exibem o seletor de modo de uso).
 * @returns {{
 *   selectedSources: Record<string, NeoFeedContextSelectionEntry>;
 *   toggleSuggestion: (suggestion: import('../../../types/articles').NeoFeedContextSuggestion) => void;
 *   removeSource: (externalArticleId: string) => void;
 *   toggleChunk: (externalArticleId: string, chunkId: string) => void;
 *   changeUsageMode: (externalArticleId: string, mode: string) => void;
 *   reset: () => void;
 * }}
 */
export default function useNeoFeedContextSelection({ forceUsageMode } = {}) {
    const [selectedSources, setSelectedSources] = useState({});

    const toggleSuggestion = useCallback((suggestion) => {
        setSelectedSources((prev) => {
            const key = suggestion.external_article_id;
            if (prev[key]) {
                const next = { ...prev };
                delete next[key];
                return next;
            }
            return { ...prev, [key]: createSelectionEntry(suggestion, { forceUsageMode }) };
        });
    }, [forceUsageMode]);

    const removeSource = useCallback((externalArticleId) => {
        setSelectedSources((prev) => {
            const next = { ...prev };
            delete next[externalArticleId];
            return next;
        });
    }, []);

    const toggleChunk = useCallback((externalArticleId, chunkId) => {
        setSelectedSources((prev) => {
            const entry = prev[externalArticleId];
            if (!entry) return prev;
            const selectedChunkIds = entry.selected_chunk_ids.includes(chunkId)
                ? entry.selected_chunk_ids.filter((id) => id !== chunkId)
                : [...entry.selected_chunk_ids, chunkId];
            return { ...prev, [externalArticleId]: { ...entry, selected_chunk_ids: selectedChunkIds } };
        });
    }, []);

    const changeUsageMode = useCallback((externalArticleId, mode) => {
        setSelectedSources((prev) => {
            const entry = prev[externalArticleId];
            if (!entry) return prev;
            return { ...prev, [externalArticleId]: { ...entry, usage_mode: mode } };
        });
    }, []);

    const reset = useCallback(() => {
        setSelectedSources({});
    }, []);

    return { selectedSources, toggleSuggestion, removeSource, toggleChunk, changeUsageMode, reset };
}
